import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FlatfinderEstate,
  FlatfinderPerspective,
  FlatfinderRegion,
} from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import { getFlatfinderAvailabilityColors, getFlatfinderImageUrl } from './display';

function HoveredEstateLabel({
  estate,
  region,
  imageWidth,
  imageHeight,
}: {
  estate: FlatfinderEstate;
  region: FlatfinderRegion;
  imageWidth: number;
  imageHeight: number;
}) {
  const label = estate.name.length > 24 ? `${estate.name.slice(0, 23)}…` : estate.name;
  const fontSize = Math.max(14, Math.min(imageWidth, imageHeight) * 0.028);
  const padding = fontSize * 0.8;
  const statusSpace = estate.availability ? fontSize * 1.4 : 0;
  const width = Math.min(
    imageWidth * 0.48,
    Math.max(fontSize * 6, label.length * fontSize * 0.62 + padding * 2 + statusSpace)
  );
  const height = fontSize * 2.4;
  const centerX =
    (region.points.reduce((sum, point) => sum + point.x, 0) / region.points.length) * imageWidth;
  const centerY =
    (region.points.reduce((sum, point) => sum + point.y, 0) / region.points.length) * imageHeight;
  const x = Math.max(fontSize, Math.min(imageWidth - width - fontSize, centerX - width / 2));
  const above = centerY - height - fontSize > 0;
  const y = above ? centerY - height - fontSize * 0.65 : centerY + fontSize * 0.65;
  const pointerX = Math.max(x + fontSize, Math.min(x + width - fontSize, centerX));
  const colors = getFlatfinderAvailabilityColors(estate.availability);

  return (
    <g pointerEvents="none">
      <rect
        fill="white"
        fillOpacity={0.96}
        height={height}
        rx={height * 0.28}
        style={{ filter: 'drop-shadow(0 4px 8px rgb(15 23 42 / 0.22))' }}
        width={width}
        x={x}
        y={y}
      />
      <polygon
        fill="white"
        fillOpacity={0.96}
        points={
          above
            ? `${pointerX - fontSize * 0.45},${y + height - 1} ${pointerX + fontSize * 0.45},${y + height - 1} ${centerX},${centerY}`
            : `${pointerX - fontSize * 0.45},${y + 1} ${pointerX + fontSize * 0.45},${y + 1} ${centerX},${centerY}`
        }
      />
      {estate.availability ? (
        <circle
          cx={x + padding + fontSize * 0.35}
          cy={y + height / 2}
          fill={colors.fill}
          r={fontSize * 0.34}
        />
      ) : null}
      <text
        dominantBaseline="middle"
        fill="#334155"
        fontSize={fontSize}
        fontWeight={600}
        x={x + padding + statusSpace}
        y={y + height / 2}
      >
        {label}
      </text>
    </g>
  );
}

type PerspectiveSceneProps = {
  authenticatedPreview: boolean;
  estatesById: Map<string, FlatfinderEstate>;
  hoveredEstate?: FlatfinderEstate;
  hoveredEstateId: string | null;
  hoveredRegion?: FlatfinderRegion;
  perspective: FlatfinderPerspective;
  active: boolean;
  publicId: string | null;
  authenticatedProjectId: string | null;
  visibleEstateIds: Set<string>;
  onHoverEstate: (estateId: string | null) => void;
  onSelectEstate: (estateId: string) => void;
};

function PerspectiveScene({
  authenticatedPreview,
  estatesById,
  hoveredEstate,
  hoveredEstateId,
  hoveredRegion,
  perspective,
  active,
  publicId,
  authenticatedProjectId,
  visibleEstateIds,
  onHoverEstate,
  onSelectEstate,
}: PerspectiveSceneProps) {
  const { t } = useTranslation();
  const imageUrl = getFlatfinderImageUrl(perspective.mediaId, publicId, authenticatedProjectId);
  const [decoded, setDecoded] = useState(false);
  const [svgLoaded, setSvgLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const ready = decoded && svgLoaded && !failed;

  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    if (authenticatedPreview) image.crossOrigin = 'use-credentials';
    image.src = imageUrl;
    image.decode().then(
      () => {
        if (!cancelled) setDecoded(true);
      },
      () => {
        if (!cancelled) setFailed(true);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [authenticatedPreview, imageUrl]);

  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <svg
        aria-hidden={!active || !ready}
        aria-label={perspective.name}
        className={`h-full max-h-full w-full max-w-full ${active && ready ? 'pointer-events-auto' : 'invisible'}`}
        role="img"
        viewBox={`0 0 ${perspective.width} ${perspective.height}`}
      >
        <image
          crossOrigin={authenticatedPreview ? 'use-credentials' : undefined}
          height={perspective.height}
          href={imageUrl}
          onError={() => setFailed(true)}
          onLoad={() => setSvgLoaded(true)}
          width={perspective.width}
          x="0"
          y="0"
        />
        {perspective.regions.map((region) => {
          if (!region.estateId || !visibleEstateIds.has(region.estateId)) return null;
          const estate = estatesById.get(region.estateId);
          if (!estate) return null;
          const points = region.points
            .map((point) => `${point.x * perspective.width},${point.y * perspective.height}`)
            .join(' ');

          return (
            <a
              aria-label={estate.name}
              href={`#flatfinder-estate-${estate.id}`}
              key={region.id}
              onBlur={() => onHoverEstate(null)}
              onClick={(event) => {
                event.preventDefault();
                onSelectEstate(estate.id);
              }}
              onFocus={() => onHoverEstate(estate.id)}
              onMouseEnter={() => onHoverEstate(estate.id)}
              onMouseLeave={() => onHoverEstate(null)}
              tabIndex={active && ready ? 0 : -1}
            >
              <polygon
                className="cursor-pointer outline-none transition-opacity focus:opacity-100"
                fill={region.color}
                fillOpacity={0.28}
                style={{ filter: hoveredEstateId === estate.id ? 'brightness(1.3)' : undefined }}
                points={points}
                stroke={region.color}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            </a>
          );
        })}
        {active && hoveredEstate && hoveredRegion ? (
          <HoveredEstateLabel
            estate={hoveredEstate}
            imageHeight={perspective.height}
            imageWidth={perspective.width}
            region={hoveredRegion}
          />
        ) : null}
      </svg>
      {active && !ready ? (
        <output className="absolute flex flex-col items-center gap-3 px-6 text-center text-sm text-slate-700">
          {!failed && <Spinner aria-hidden="true" className="size-10" />}
          {failed ? t('flatfinderPerspectiveImageError') : t('loading')}
        </output>
      ) : null}
    </div>
  );
}

export function ViewerPerspective({
  authenticatedPreview,
  estatesById,
  hoveredEstate,
  hoveredEstateId,
  hoveredRegion,
  perspectiveIndex,
  perspectives,
  publicId,
  authenticatedProjectId,
  visibleEstateIds,
  onHoverEstate,
  onNextPerspective,
  onPreviousPerspective,
  onSelectEstate,
}: Omit<PerspectiveSceneProps, 'perspective' | 'active'> & {
  perspectiveIndex: number;
  perspectives: FlatfinderPerspective[];
  onNextPerspective: () => void;
  onPreviousPerspective: () => void;
}) {
  const { t } = useTranslation();
  const perspective = perspectives[perspectiveIndex];

  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="relative flex min-h-[320px] items-center justify-center overflow-hidden bg-slate-200 sm:min-h-[480px] lg:h-[calc(100vh-190px)] lg:min-h-[560px]">
        <p
          aria-live="polite"
          className="pointer-events-none absolute left-3 top-3 z-10 max-w-[calc(100%-1.5rem)] break-words rounded-md bg-white/90 px-3 py-2 text-sm font-medium text-slate-900 shadow-sm backdrop-blur-sm"
        >
          {perspective.name}
        </p>
        {perspectives.length > 1 ? (
          <>
            <Button
              aria-label={t('previousPerspective')}
              title={t('previousPerspective')}
              variant="ghost"
              className="absolute left-3 top-1/2 z-10 !h-9 !w-9 min-w-9 -translate-y-1/2 border-0 bg-white/90 p-0 text-slate-900 shadow-sm backdrop-blur-sm hover:border-0 hover:bg-white lg:p-0 [&_svg]:size-4"
              onClick={onPreviousPerspective}
            >
              <ChevronLeft />
            </Button>
            <Button
              aria-label={t('nextPerspective')}
              title={t('nextPerspective')}
              variant="ghost"
              className="absolute right-3 top-1/2 z-10 !h-9 !w-9 min-w-9 -translate-y-1/2 border-0 bg-white/90 p-0 text-slate-900 shadow-sm backdrop-blur-sm hover:border-0 hover:bg-white lg:p-0 [&_svg]:size-4"
              onClick={onNextPerspective}
            >
              <ChevronRight />
            </Button>
          </>
        ) : null}
        {perspectives.map((item, index) => (
          <PerspectiveScene
            active={index === perspectiveIndex}
            authenticatedPreview={authenticatedPreview}
            authenticatedProjectId={authenticatedProjectId}
            estatesById={estatesById}
            hoveredEstate={hoveredEstate}
            hoveredEstateId={hoveredEstateId}
            hoveredRegion={hoveredRegion}
            key={`${item.id}-${getFlatfinderImageUrl(item.mediaId, publicId, authenticatedProjectId)}-${authenticatedPreview}`}
            perspective={item}
            publicId={publicId}
            visibleEstateIds={visibleEstateIds}
            onHoverEstate={onHoverEstate}
            onSelectEstate={onSelectEstate}
          />
        ))}
      </div>
    </section>
  );
}
